import {
  toolCallResultSchema,
  ToolCallResultStatus,
} from 'common/src/modules/creativeTemplates/copilot/copilotCreativeTemplates.consts';
import {
  MovieFillType,
  MovieLineCap,
  MovieLineJoin,
  MovieShapeLayerType,
  MovieStrokeType,
} from 'common/src/modules/movie/movie.enums';
import {
  IShape,
  ShapeEllipse,
  ShapePath,
  ShapePolystar,
  ShapeRect,
} from 'common/src/modules/movie/movieShapes.types';
import simpleMovieValue from 'common/src/modules/movie/utils/simpleMovieValue';
import appendCustomCallerToCallChain from 'common/src/modules/shared/copilot/utils/creators/appendCustomCallerToCallChain';
import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';
import openAIUserMessage from 'common/src/modules/shared/copilot/openAI/utils/openAIUserMessage';
import {Caller, ToolUtils} from 'common/src/modules/shared/copilot/copilot.types';
import {hexToRgbaArray} from 'common/src/utils/colorUtils/hexToRgbaArray';
import {shapeSchema} from '../adBuilderAgent/adBuilderAgent.schemas';
import {extractShapeToSchema} from './extractShapeToSchema';

export interface ShapeContentResult {
  shape: IShape;
  shapeType: string;
  isModification?: boolean;
  unsupportedRequest?: string;
}

export default async function getShapeContentFromPrompt({
  shapeDescription,
  existingShape,
  time = 0,
  toolUtils,
  callChain,
}: {
  shapeDescription: string;
  existingShape?: IShape;
  time?: number;
  toolUtils: ToolUtils;
  callChain: Caller[];
}): Promise<ShapeContentResult> {
  const isModification = !!existingShape;

  // Extract current shape context for modifications
  let currentShapeContext = '';

  if (isModification) {
    const shapeContext = extractShapeToSchema(existingShape, time);
    currentShapeContext = JSON.stringify(shapeContext, null, 2);
  }

  // Build context-aware system prompt
  const systemPrompt = isModification
    ? `You are a shape modifier. You modify existing shapes based on natural language descriptions.

Current shape context:
${currentShapeContext}

When modifying shapes:
- Preserve existing properties unless explicitly requested to change
- Only modify the properties mentioned in the user's request
- Maintain the same shape type unless explicitly asked to change it
- Keep existing colors, dimensions, etc. unless specifically mentioned

- Each type of shape has its own geometry property while the fill, stroke and opacity properties are common to all.
- Colors are hex 6-digit format (e.g., "#FF0000")
- If user requests "no fill" or "transparent fill", set fillColor to null
- If user requests "no stroke" or "no outline", set strokeColor to null
- At least one of fillColor or strokeColor must be provided (cannot both be null)
- IMPORTANT: The shape coordinates you generate relative to the layer transform position. and for rectangles, ellipses, and polygons, its the center of the shape and not the top-left corner.


Example modification output:
{
  "type": "rectangle",
  "properties": {
    "fillColor": "#0000FF",
    "strokeColor": "#000000", 
    "strokeWidth": 2,
    "opacity": 80
  },
  "rectangle": {
    "width": 300,
    "height": 100,
    "cornerRadius": 10,
    "x": 50,
    "y": 25
  }
}`
    : `You are a shape generator. Convert natural language descriptions or any specification into structured shape configurations.

- Each type of shape has its own geometry property while the fill, stroke and opacity properties are common to all.
- Colors are hex 6-digit format (e.g., "#FF0000")
- If user requests "no fill" or "transparent fill", set fillColor to null
- If user requests "no stroke" or "no outline", set strokeColor to null
- At least one of fillColor or strokeColor must be provided (cannot both be null)
- IMPORTANT: The shape coordinates you generate relative to the layer transform position. and for rectangles, ellipses, and polygons, its the center of the shape and not the top-left corner.

Example output:
{
  "type": "rectangle",
  "properties": {
    "fillColor": "#FF0000",
    "strokeColor": "#000000",
    "strokeWidth": 2,
    "opacity": 80
  },
  "rectangle": {
    "width": 200,
    "height": 100,
    "cornerRadius": 10,
    "x": 0,
    "y": 0
  }
}`;

  // Build context-aware user prompt
  const userPrompt = isModification
    ? `Modify the existing shape based on: ${shapeDescription}.
    
Keep all existing properties unchanged unless specifically mentioned in the request.
Return the complete shape configuration with both modified and unchanged properties.`
    : `Generate shape configuration for: ${shapeDescription}.
    
Create structured shape with appropriate geometry, colors, and styling based on the description.`;

  // Use LLM to generate shape content
  const response = await toolUtils.invokeStandaloneLLMCall({
    messages: [openAISystemMessage(systemPrompt), openAIUserMessage(userPrompt)],
    responseSchema: toolCallResultSchema.extend({
      result: shapeSchema,
    }),
    buildResponseUsingToolCall: true,
    callChain: appendCustomCallerToCallChain('getShapeContentFromPrompt', callChain),
  });

  // Check if LLM indicated failure
  if (response.status === ToolCallResultStatus.Failure) {
    const reason = response.failureReason || 'Request failed without specific reason';
    throw new Error(`Shape generation failed: ${reason}`);
  }

  const shapeContent = response.result;

  // Validate shape content
  if (shapeContent.properties.fillColor === null && shapeContent.properties.strokeColor === null) {
    throw new Error('Shape must have either a fill or a stroke');
  }

  const shapeProps = shapeContent.properties;

  // Validation for shape-specific parameters
  switch (shapeContent.type) {
    case 'rectangle':
      if (!shapeContent.rectangle) {
        throw new Error('Rectangle properties are required for rectangle shapes');
      }

      break;
    case 'ellipse':
      if (!shapeContent.ellipse) {
        throw new Error('Ellipse properties are required for ellipse shapes');
      }

      break;
    case 'polygon':
      if (!shapeContent.polygon) {
        throw new Error('Polygon properties are required for polygon shapes');
      }

      break;
    case 'path':
      if (!shapeContent.path?.points || shapeContent.path.points.length < 2) {
        throw new Error('At least 2 points are required for path shapes');
      }

      break;
    default:
      throw new Error(`Unsupported shape type: ${shapeContent.type}`);
  }

  const baseShape: Partial<IShape> = {
    tr: {
      p: simpleMovieValue([0, 0]),
      s: simpleMovieValue([1, 1]),
      an: simpleMovieValue([0, 0]),
      r: simpleMovieValue([0]),
      sk: simpleMovieValue([0]),
      skx: simpleMovieValue([0]),
      a: simpleMovieValue([shapeProps.opacity / 100 || 1]),
    },
    fill:
      shapeProps.fillColor !== null
        ? {
            t: MovieFillType.Solid,
            o: {c: simpleMovieValue(hexToRgbaArray(shapeProps.fillColor))},
            a: simpleMovieValue([1]),
          }
        : null,
    stroke: !!shapeProps.strokeColor
      ? {
          t: MovieFillType.Solid,
          o: {c: simpleMovieValue(hexToRgbaArray(shapeProps.strokeColor))},
          a: simpleMovieValue([1]),
          w: simpleMovieValue([shapeProps.strokeWidth || 2]),
          lj: MovieLineJoin.Miter,
          lc: MovieLineCap.Butt,
          lm: 4,
          align: shapeProps.strokeAlign ?? MovieStrokeType.Center,
        }
      : null,
  };

  let shape: IShape;

  switch (shapeContent.type) {
    case 'rectangle':
      const rectConfig = shapeContent.rectangle;
      shape = {
        ...baseShape,
        ty: MovieShapeLayerType.Rectangle,
        p: simpleMovieValue([rectConfig.x || 0, rectConfig.y || 0]),
        s: simpleMovieValue([rectConfig.width, rectConfig.height]),
        r: simpleMovieValue([rectConfig.cornerRadius || 0]),
      } as ShapeRect;
      break;

    case 'ellipse':
      const ellipseConfig = shapeContent.ellipse;
      shape = {
        ...baseShape,
        ty: MovieShapeLayerType.Ellipse,
        p: simpleMovieValue([0, 0]),
        s: simpleMovieValue([ellipseConfig.width, ellipseConfig.height]),
      } as ShapeEllipse;
      break;

    case 'polygon':
      const polygonConfig = shapeContent.polygon;
      shape = {
        ...baseShape,
        ty: MovieShapeLayerType.Polystar,
        type: polygonConfig.innerRadius ? 1 : 0,
        p: simpleMovieValue([0, 0]),
        pts: simpleMovieValue([polygonConfig.sides]),
        iRad: simpleMovieValue([polygonConfig.innerRadius || 0]),
        iRo: simpleMovieValue([0]),
        oRad: simpleMovieValue([polygonConfig.outerRadius]),
        oRo: simpleMovieValue([0]),
      } as ShapePolystar;
      break;

    case 'path':
      const pathConfig = shapeContent.path;
      shape = {
        ...baseShape,
        ty: MovieShapeLayerType.Path,
        path: {
          val: {
            v: pathConfig.points.map((p) => [p.x, p.y]),
            o: pathConfig.points.map((p) => [p.ox || 0, p.oy || 0]),
            i: pathConfig.points.map((p) => [p.ix || 0, p.iy || 0]),
          },
          c: pathConfig.isClosed,
        },
      } as ShapePath;
      break;

    default:
      throw new Error(`Unsupported shape type: ${shapeContent.type}`);
  }

  return {
    shape,
    shapeType: shapeContent.type,
    isModification,
    ...(response.unsupportedRequestPart && {unsupportedRequest: response.unsupportedRequestPart}),
  };
}
